import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import ThreatMonitoringDashboard from './components/ThreatMonitoringDashboard';
import EscalationWorkflow from './components/EscalationWorkflow';
import CommunicationPanel from './components/CommunicationPanel';
import ResponseTrackingDashboard from './components/ResponseTrackingDashboard';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const CrisisResponseCenter = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedThreat, setSelectedThreat] = useState(null);
  const [activeView, setActiveView] = useState('monitoring');
  const [emergencyMode, setEmergencyMode] = useState(false);

  const viewOptions = [
    {
      id: 'monitoring',
      label: 'Threat Monitoring',
      icon: 'Shield',
      description: 'Real-time FIMI detection'
    },
    {
      id: 'escalation',
      label: 'Escalation Workflow',
      icon: 'GitBranch',
      description: 'Response coordination'
    },
    {
      id: 'communication',
      label: 'Strategic Communications',
      icon: 'MessageSquare',
      description: 'Blueprint alignment'
    },
    {
      id: 'tracking',
      label: 'Response Tracking',
      icon: 'BarChart3',
      description: 'Platform compliance'
    }
  ];

  const emergencyActions = [
    {
      id: 'democracy-shield',
      label: 'Democracy Shield Funding',
      icon: 'Shield',
      description: 'Trigger emergency funding protocols',
      status: 'ready'
    },
    {
      id: 'crisis-escalation',
      label: 'Crisis Escalation',
      icon: 'AlertTriangle',
      description: 'Activate highest priority response',
      status: 'ready'
    },
    {
      id: 'public-alert',
      label: 'Public Alert System',
      icon: 'Megaphone',
      description: 'Issue coordinated public warnings',
      status: 'ready'
    },
    {
      id: 'platform-emergency',
      label: 'Platform Emergency Protocol',
      icon: 'Link',
      description: 'Direct VLOP emergency contacts',
      status: 'ready'
    }
  ];

  const handleThreatSelection = (threat) => {
    setSelectedThreat(threat);
    if (activeView === 'monitoring') {
      setActiveView('escalation');
    }
  };

  const handleEmergencyToggle = () => {
    setEmergencyMode(!emergencyMode);
    if (!emergencyMode) {
      // Log emergency mode activation
      console.log('Emergency mode activated at:', new Date()?.toISOString());
    }
  };

  const renderMainContent = () => {
    switch (activeView) {
      case 'monitoring':
        return (
          <ThreatMonitoringDashboard 
            onThreatSelect={handleThreatSelection}
          />
        );
      case 'escalation':
        return (
          <EscalationWorkflow 
            selectedThreat={selectedThreat}
          />
        );
      case 'communication':
        return <CommunicationPanel />;
      case 'tracking':
        return <ResponseTrackingDashboard />;
      default:
        return <ThreatMonitoringDashboard onThreatSelect={handleThreatSelection} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main className={`transition-all duration-300 ${
        sidebarCollapsed ? 'ml-16' : 'ml-64'
      } pt-16`}>
        <div className="p-6">
          {/* Crisis Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Icon name="Shield" size={24} className="text-red-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Crisis Response Center</h1>
                  <p className="text-gray-600">FIMI Threat Coordination & Institutional Response</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                {/* Emergency Mode Toggle */}
                <Button
                  variant={emergencyMode ? "destructive" : "outline"}
                  iconName={emergencyMode ? "AlertTriangle" : "Shield"}
                  iconPosition="left"
                  onClick={handleEmergencyToggle}
                  className={emergencyMode ? "animate-pulse" : ""}
                >
                  {emergencyMode ? "Emergency Active" : "Emergency Mode"}
                </Button>
                
                {/* Real-time Status */}
                <div className="flex items-center space-x-2 px-3 py-2 bg-green-50 rounded-lg border border-green-200">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-sm font-medium text-green-700">
                    System Operational
                  </span>
                </div>
              </div>
            </div>

            {/* Emergency Actions Bar */}
            {emergencyMode && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertTriangle" size={20} className="text-red-600" />
                    <h3 className="text-lg font-semibold text-red-900">Emergency Protocols Active</h3>
                  </div>
                  <span className="text-sm text-red-600 font-mono">
                    {new Date()?.toLocaleString()}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {emergencyActions?.map((action) => (
                    <Button
                      key={action?.id}
                      variant="outline"
                      size="sm"
                      iconName={action?.icon}
                      iconPosition="left"
                      className="justify-start text-left h-auto py-3 border-red-300 hover:bg-red-100"
                      onClick={() => {
                        alert(`${action?.label} protocol activated`);
                      }}
                    >
                      <div>
                        <div className="font-medium text-red-900">{action?.label}</div>
                        <div className="text-xs text-red-600 mt-1">{action?.description}</div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* View Navigation */}
            <div className="flex items-center space-x-1 bg-white rounded-lg border border-gray-200 p-1">
              {viewOptions?.map((view) => (
                <button
                  key={view?.id}
                  onClick={() => setActiveView(view?.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    activeView === view?.id
                      ? 'bg-blue-100 text-blue-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon name={view?.icon} size={16} />
                  <div className="text-left">
                    <div className="font-medium">{view?.label}</div>
                    <div className="text-xs opacity-75">{view?.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="space-y-6">
            {renderMainContent()}
          </div>

          {/* Quick Stats Footer */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-blue-600 mb-1">7</div>
              <div className="text-sm text-gray-600">Active Threats</div>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-green-600 mb-1">24</div>
              <div className="text-sm text-gray-600">Response Teams</div>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-purple-600 mb-1">147</div>
              <div className="text-sm text-gray-600">Platform Actions</div>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-orange-600 mb-1">89%</div>
              <div className="text-sm text-gray-600">Avg Compliance</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CrisisResponseCenter;